import { ICONS } from '../../../common/constants'
import fetchAPI from '../../../common/fetchAPI'
import useModal from '../../../common/hooks/useModal'
import useToast from '../../../common/hooks/useToast'
import ResourceList from '../../../components/admin/ResourceList'
import ConfirmModalContent from '../../../components/common/ConfirmModalContent'
import useData from '../../../common/hooks/useData'
import { useParams } from 'react-router-dom'
import Spinner from '../../../components/common/Spinner'
import ErrorMessage from '../../../components/common/ErrorMessage'
import InlineStack from '../../../components/common/InlineStack'
import Button from '../../../components/common/Button'
import SectionContainer from '../../../components/common/SectionContainer'
import FormModalContent from '../../../components/common/FormModalContent'

export default function ContactFormRecipientsList({ enablePullToRefresh }) {
    const { contactFormId } = useParams()
    const setToast = useToast()
    const { setModal, setModalLoading, isModalLoading } = useModal()

    const { form, formLoading, formError, formMutate } = useData(
        `/v1/contact-forms/${contactFormId}?fields[]=otherRecipientEmails`,
        'form',
    )

    function handleNewItemAdd() {
        setModal(
            <FormModalContent
                inputs={[
                    {
                        key: 'email',
                        label: 'Email',
                        type: 'email',
                        required: true,
                    },
                ]}
                confirmText="Save"
                onConfirm={async (data, setError) => {
                    setError('')
                    if (isModalLoading) return
                    setModalLoading(true)
                    const { error, meta } = await fetchAPI(
                        `/v1/contact-forms/${contactFormId}`,
                        {
                            otherRecipientEmails: Array.from(
                                new Set([
                                    ...(form.otherRecipientEmails || []),
                                    data.email,
                                ]),
                            ),
                        },
                        'PATCH',
                    )
                    setModalLoading(false)
                    if (error) {
                        setError(error, meta?.fields?.length ? [true] : [])
                        return
                    }
                    setModal(null)
                    setToast('Email updated')
                    formMutate()
                }}
                onCancel={() => setModal(null)}
            />,
            'Set task order',
        )
    }

    function handleEdit(item) {
        setModal(
            <FormModalContent
                inputs={[
                    {
                        key: 'email',
                        label: 'Email',
                        type: 'email',
                        required: true,
                        defaultValue: item,
                    },
                ]}
                confirmText="Save"
                onConfirm={async (data, setError) => {
                    setError('')
                    if (isModalLoading) return
                    setModalLoading(true)
                    const updatedEmails = form.otherRecipientEmails.map(
                        (email) => (email === item ? data.email : email),
                    )
                    const { error, meta } = await fetchAPI(
                        `/v1/contact-forms/${contactFormId}`,
                        {
                            otherRecipientEmails: Array.from(
                                new Set(updatedEmails),
                            ),
                        },
                        'PATCH',
                    )
                    setModalLoading(false)
                    if (error) {
                        setError(error, meta?.fields?.length ? [true] : [])
                        return
                    }
                    setModal(null)
                    setToast('Email updated')
                    formMutate()
                }}
                onCancel={() => setModal(null)}
            />,
            'Edit email',
        )
    }

    function handleDelete(item) {
        setModal(
            <ConfirmModalContent
                text={`Are you sure you want to remove "${item}"?`}
                confirmText="Delete"
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const updatedEmails = (
                        form.otherRecipientEmails || []
                    ).filter((email) => email !== item)
                    const { error } = await fetchAPI(
                        `/v1/contact-forms/${contactFormId}`,
                        {
                            otherRecipientEmails: updatedEmails,
                        },
                        'PATCH',
                    )
                    setModalLoading(false)
                    if (error) {
                        setModal(null)
                        setToast(error, 'alert')
                        return
                    }
                    setModal(null)
                    setToast('Email removed')
                    formMutate()
                }}
                onCancel={() => setModal(null)}
            />,
            'Remove email',
        )
    }

    if (formLoading) {
        return <Spinner />
    }

    if (formError) {
        return <ErrorMessage section>{formError}</ErrorMessage>
    }

    return (
        <>
            <SectionContainer>
                <InlineStack justifyEnd>
                    <Button
                        noMargin
                        small
                        text="Add new email"
                        onClick={handleNewItemAdd}
                    />
                </InlineStack>
            </SectionContainer>
            <ResourceList
                enablePullToRefresh={enablePullToRefresh}
                key={JSON.stringify(form.otherRecipientEmails)}
                items={form.otherRecipientEmails || []}
                // itemsLoading={formLoading}
                // renderMainButton
                paginated={false}
                fields={[
                    {
                        column: 1,
                        getValue: (item) => item,
                    },
                ]}
                actions={[
                    {
                        title: 'Edit',
                        onClick: (item) => handleEdit(item),
                        icon: ICONS.EDIT_GRAY,
                    },
                    {
                        title: 'Delete',
                        icon: ICONS.TRASH_RED,
                        type: 'alert',
                        onClick: (item) => handleDelete(item),
                    },
                ]}
            />
        </>
    )
}
